import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

export type CurrencyCode = "USD" | "EUR" | "GBP" | "CHF" | "ILS" | "AUD" | "CAD" | "HKD" | "JPY" | "SGD" | "TWD" | "AED";

export const CURRENCIES: { code: CurrencyCode; label: string; symbol: string }[] = [
  { code: "USD", label: "USD", symbol: "$" },
  { code: "EUR", label: "EUR", symbol: "€" },
  { code: "GBP", label: "GBP", symbol: "£" },
  { code: "CHF", label: "CHF", symbol: "CHF" },
  { code: "ILS", label: "ILS", symbol: "₪" },
  { code: "AUD", label: "AUD", symbol: "$" },
  { code: "CAD", label: "CAD", symbol: "$" },
  { code: "HKD", label: "HKD", symbol: "$" },
  { code: "JPY", label: "JPY", symbol: "¥" },
  { code: "SGD", label: "SGD", symbol: "$" },
  { code: "TWD", label: "TWD", symbol: "$" },
  { code: "AED", label: "AED", symbol: "د.إ" },
];

interface ExchangeRates {
  rates: Record<string, number>;
  fetchedAt: number; // timestamp
}

interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (c: CurrencyCode) => void;
  convert: (amountUsd: number) => number;
  formatPrice: (amount: string, _currencyCode?: string) => string;
}

const CACHE_KEY = "motai_exchange_rates";
const CURRENCY_KEY = "motai_currency";
const ONE_DAY = 24 * 60 * 60 * 1000;

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function getCachedRates(): ExchangeRates | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: ExchangeRates = JSON.parse(raw);
    if (Date.now() - parsed.fetchedAt < ONE_DAY) return parsed;
    return null;
  } catch {
    return null;
  }
}

function setCachedRates(rates: ExchangeRates) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(rates));
}

// Fallback rates in case API is unavailable
const FALLBACK_RATES: Record<string, number> = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  CHF: 0.88,
  ILS: 3.65,
  AUD: 1.53,
  CAD: 1.36,
  HKD: 7.82,
  JPY: 149.5,
  SGD: 1.34,
  TWD: 31.5,
  AED: 3.67,
};

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    return (localStorage.getItem(CURRENCY_KEY) as CurrencyCode) || "USD";
  });

  const [rates, setRates] = useState<Record<string, number>>(
    () => getCachedRates()?.rates || FALLBACK_RATES,
  );

  const setCurrency = useCallback((c: CurrencyCode) => {
    setCurrencyState(c);
    localStorage.setItem(CURRENCY_KEY, c);
  }, []);

  useEffect(() => {
    const cached = getCachedRates();
    if (cached) {
      setRates(cached.rates);
      return;
    }

    fetch("https://open.er-api.com/v6/latest/USD")
      .then((r) => r.json())
      .then((data) => {
        if (data?.rates) {
          const picked: Record<string, number> = { USD: 1 };
          for (const c of CURRENCIES) {
            if (data.rates[c.code]) picked[c.code] = data.rates[c.code];
          }
          setRates(picked);
          setCachedRates({ rates: picked, fetchedAt: Date.now() });
        }
      })
      .catch(() => {
        // Keep fallback rates
      });
  }, []);

  const convert = useCallback(
    (amountUsd: number) => {
      const rate = rates[currency] ?? 1;
      return amountUsd * rate;
    },
    [currency, rates],
  );

  const formatPrice = useCallback(
    (amount: string, _currencyCode?: string) => {
      const usdAmount = parseFloat(amount);
      if (isNaN(usdAmount)) return amount;
      const converted = convert(usdAmount);
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(converted);
    },
    [currency, convert],
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
